import { useState } from "react";
import { useNotify, useRefresh } from "ra-core";
import {
    Bot,
    Loader2,
    Mail,
    RefreshCw,
    Send,
    Sparkles,
    FileText,
    MessageSquareText
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { useEmail, EmailMode } from "./useEmail";

interface EmailComposerProps {
    contactId: number;
    contactName?: string;
    contactEmail?: string;
    className?: string;
}

export const EmailComposer = ({
    contactId,
    contactName,
    contactEmail,
    className
}: EmailComposerProps) => {
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const notify = useNotify();
    const refresh = useRefresh();

    const {
        subject,
        setSubject,
        bodyHtml,
        setBodyHtml,
        bodyText,
        setBodyText,
        mode,
        setMode,
        customPrompt,
        setCustomPrompt,
        isGenerating,
        isSending,
        error,
        generateAIEmail,
        sendEmail,
        reset,
        contextInfo,
    } = useEmail(contactId);

    const handleSend = async () => {
        if (!contactEmail) {
            notify("Contact does not have an email address", { type: "error" });
            return;
        }

        setShowConfirmDialog(false);
        const success = await sendEmail(contactEmail, contactName);
        if (success) {
            notify("Email sent successfully", { type: "success" });
            reset();
            refresh();
        } else {
            notify(error || "Failed to send email", { type: "error" });
        }
    };

    const handleGenerateClick = async () => {
        await generateAIEmail();
        if (error) {
            notify(error, { type: "error" });
        }
    };

    const handleModeChange = (value: string) => {
        setMode(value as EmailMode);
        // Reset content when switching modes
        if (value !== mode) {
            reset();
        }
    };

    const canGenerate = mode !== "manual" && (mode === "context-aware" || customPrompt.trim());
    const canSend = subject.trim() && (bodyHtml.trim() || bodyText.trim()) && contactEmail;

    return (
        <div className={cn("space-y-4 p-4 border rounded-lg bg-muted/30", className)}>
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm font-medium">
                    <Mail className="w-4 h-4 text-purple-600" />
                    <span>Compose Email</span>
                </div>
                {!contactEmail && (
                    <span className="text-xs text-destructive">No email on file</span>
                )}
            </div>

            {/* Mode Selection */}
            <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Compose Mode</Label>
                <Select value={mode} onValueChange={handleModeChange}>
                    <SelectTrigger className="w-full">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="context-aware">
                            <div className="flex items-center gap-2">
                                <MessageSquareText className="w-4 h-4 text-blue-500" />
                                <span>Context-Aware Reply</span>
                            </div>
                        </SelectItem>
                        <SelectItem value="custom-prompt">
                            <div className="flex items-center gap-2">
                                <Sparkles className="w-4 h-4 text-amber-500" />
                                <span>Custom AI Prompt</span>
                            </div>
                        </SelectItem>
                        <SelectItem value="manual">
                            <div className="flex items-center gap-2">
                                <FileText className="w-4 h-4 text-gray-500" />
                                <span>Write Manually</span>
                            </div>
                        </SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* Custom Prompt (only for custom-prompt mode) */}
            {mode === "custom-prompt" && (
                <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">
                        What should this email accomplish?
                    </Label>
                    <Textarea
                        value={customPrompt}
                        onChange={(e) => setCustomPrompt(e.target.value)}
                        placeholder="e.g., Follow up on our meeting last week and propose next steps..."
                        rows={2}
                        disabled={isGenerating}
                    />
                </div>
            )}

            {/* Context Info (for context-aware mode) */}
            {mode === "context-aware" && contextInfo && (
                <div className="text-xs text-muted-foreground bg-blue-50 dark:bg-blue-950 p-2 rounded">
                    📧 Using {contextInfo.messagesIncluded} message(s) as context
                    {contextInfo.lastMessageDate && (
                        <span> • Last: {new Date(contextInfo.lastMessageDate).toLocaleDateString()}</span>
                    )}
                </div>
            )}

            {/* Generate Button */}
            {mode !== "manual" && (
                <Button
                    variant="outline"
                    size="sm"
                    onClick={handleGenerateClick}
                    disabled={isGenerating || isSending || !canGenerate}
                    className="w-full gap-1.5"
                >
                    {isGenerating ? (
                        <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Generating...
                        </>
                    ) : subject ? (
                        <>
                            <RefreshCw className="w-4 h-4" />
                            Regenerate
                        </>
                    ) : (
                        <>
                            <Bot className="w-4 h-4" />
                            Generate with AI
                        </>
                    )}
                </Button>
            )}

            {/* Subject Line */}
            <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Subject</Label>
                <Input
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Email subject..."
                    disabled={isGenerating || isSending}
                />
            </div>

            {/* Email Body */}
            <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Message</Label>
                <Textarea
                    value={bodyText || bodyHtml.replace(/<[^>]*>/g, "")}
                    onChange={(e) => {
                        setBodyText(e.target.value);
                        setBodyHtml(`<p>${e.target.value.replace(/\n/g, "</p><p>")}</p>`);
                    }}
                    placeholder="Write your message..."
                    rows={6}
                    className="resize-none"
                    disabled={isGenerating || isSending}
                />
            </div>

            {/* Error Display */}
            {error && (
                <div className="text-xs text-destructive bg-destructive/10 p-2 rounded">
                    {error}
                </div>
            )}

            {/* Send Button */}
            <div className="flex justify-end">
                <Button
                    size="sm"
                    onClick={() => setShowConfirmDialog(true)}
                    disabled={!canSend || isGenerating || isSending}
                    className="gap-1.5"
                >
                    {isSending ? (
                        <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Sending...
                        </>
                    ) : (
                        <>
                            <Send className="w-4 h-4" />
                            Send Email
                        </>
                    )}
                </Button>
            </div>

            {/* Confirmation Dialog */}
            <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Confirm Email</DialogTitle>
                        <DialogDescription>
                            Send this email to {contactName || contactEmail}?
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-3 my-4">
                        <div>
                            <Label className="text-xs text-muted-foreground">To:</Label>
                            <p className="text-sm font-medium">{contactEmail}</p>
                        </div>
                        <div>
                            <Label className="text-xs text-muted-foreground">Subject:</Label>
                            <p className="text-sm font-medium">{subject}</p>
                        </div>
                        <div>
                            <Label className="text-xs text-muted-foreground">Message:</Label>
                            <div className="p-3 bg-muted rounded-md text-sm max-h-48 overflow-y-auto">
                                {bodyText || bodyHtml.replace(/<[^>]*>/g, "")}
                            </div>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleSend} disabled={isSending}>
                            {isSending ? "Sending..." : "Send Email"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};
